import { z } from 'zod'

export const LEARNING_LMENGINE_EVENT_CONTRACT_VERSION = '1.0.0' as const
export const LMENGINE_EVENT_SCHEMA_VERSION = 'lmengine-event-v1' as const
export const LMENGINE_EVENT_TYPE = 'xapi_statement_accepted' as const

const requiredText = z.string().trim().min(1)
const offsetTimestamp = z.string().datetime({ offset: true })
const finiteNumber = z.number().finite()

export const lmengineEventSubjectSchema = z.object({
  userId: requiredText.max(240),
  provider: requiredText.max(120),
  providerSubjectId: requiredText.max(320),
  issuer: requiredText.optional(),
  audience: requiredText.optional(),
}).strict()

export const lmengineEventActorSchema = z.object({
  account: z.object({
    homePage: requiredText.max(2048),
    name: requiredText.max(320),
  }).strict(),
}).strict()

export const lmengineEventVerbSchema = z.object({
  id: requiredText.max(2048),
}).strict()

const extensionValue = z.union([z.string(), finiteNumber, z.boolean()])

export const lmengineEventObjectSchema = z.object({
  id: requiredText.max(2048),
  definition: z.object({
    type: requiredText.max(2048).optional(),
    extensions: z.record(extensionValue).optional(),
  }).strict().optional(),
}).strict()

export const lmengineEventScoreSchema = z.object({
  max: finiteNumber.optional(),
  min: finiteNumber.optional(),
  raw: finiteNumber.optional(),
  scaled: finiteNumber.optional(),
}).strict()

export const lmengineEventResultSchema = z.object({
  completion: z.boolean().optional(),
  success: z.boolean().optional(),
  duration: requiredText.max(128).optional(),
  score: lmengineEventScoreSchema.optional(),
}).strict()

export const lmengineEventContextSchema = z.object({
  language: requiredText.max(64).optional(),
  platform: requiredText.max(240).optional(),
  registration: requiredText.max(240).optional(),
}).strict()

export const lmengineEventPayloadSchema = z.object({
  statementId: requiredText.max(240),
  actor: lmengineEventActorSchema,
  verb: lmengineEventVerbSchema,
  object: lmengineEventObjectSchema,
  result: lmengineEventResultSchema.optional(),
  context: lmengineEventContextSchema.optional(),
  stored: requiredText.max(512),
}).strict()

/** The event receiver accepts one canonical nested event, not `{ events: [...] }`. */
export const lmengineEventSchema = z.object({
  eventType: z.literal(LMENGINE_EVENT_TYPE),
  schemaVersion: z.literal(LMENGINE_EVENT_SCHEMA_VERSION),
  occurredAt: offsetTimestamp,
  subject: lmengineEventSubjectSchema,
  payload: lmengineEventPayloadSchema,
}).strict()

export const learningLmengineEventRequestSchema = lmengineEventSchema
export const learningLmengineEventSchema = lmengineEventSchema
export const learningLmengineEventResponseSchema = z.object({
  contract_version: z.string().min(1),
  accepted: z.number().int().nonnegative().optional(),
  duplicates: z.number().int().nonnegative().optional(),
  rejected: z.number().int().nonnegative().optional(),
  quarantined: z.number().int().nonnegative().optional(),
  results: z.array(z.record(z.unknown())).optional(),
}).strict()
export const learningLmengineEventIngestRequestSchema = lmengineEventSchema
export const learningLmengineEventIngestResponseSchema = learningLmengineEventResponseSchema

export type LearningLmengineEvent = z.infer<typeof learningLmengineEventSchema>
export type LearningLmengineEventIngestRequest = z.infer<typeof learningLmengineEventIngestRequestSchema>
export type LearningLmengineEventIngestResponse = z.infer<typeof learningLmengineEventIngestResponseSchema>
export type LmengineEventSubject = z.infer<typeof lmengineEventSubjectSchema>
export type LmengineEventPayload = z.infer<typeof lmengineEventPayloadSchema>
export type LmengineEvent = z.infer<typeof lmengineEventSchema>
export type LearningLmengineEventRequest = z.infer<typeof learningLmengineEventRequestSchema>

// PascalCase aliases keep this package compatible with the learning worker's
// application contract naming while generated families adopt the package names.
export const LmengineEventSchema = lmengineEventSchema
export const LmengineEventRequestSchema = learningLmengineEventRequestSchema
export const LmengineEventResponseSchema = learningLmengineEventResponseSchema
